import type { ChangeRequestMode, ChangeRequestPartKind } from "@corca/tiptap";
import type { CommandProps, Editor } from "@tiptap/core";
import type { Fragment, Node as PMNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";

export type NormalizedChangeRequest = {
  mode: ChangeRequestMode;
  before: Fragment | null;
  after: Fragment | null;
};

export function getChangeRequestPartKind(node: PMNode): ChangeRequestPartKind {
  const kind = node.attrs.kind as unknown;
  return kind === "before" ? "before" : "after";
}

export function getChangeRequestMode(node: PMNode): ChangeRequestMode {
  const mode = node.attrs.mode as unknown;
  if (mode === "replace" || mode === "delete") {
    return mode;
  }

  return "insert";
}

export function normalizeChangeRequest(node: PMNode): NormalizedChangeRequest | null {
  let before: Fragment | null = null;
  let after: Fragment | null = null;

  node.forEach((child) => {
    if (child.type.name !== "changeRequestPart") {
      return;
    }

    if (getChangeRequestPartKind(child) === "before") {
      before = child.content;
    } else {
      after = child.content;
    }
  });

  if (!before && !after) {
    return null;
  }

  return { mode: getChangeRequestMode(node), before, after };
}

export function findChangeRequest(
  state: CommandProps["state"],
  id: string,
): { from: number; to: number; node: PMNode } | null {
  let result: { from: number; to: number; node: PMNode } | null = null;

  state.doc.descendants((node, pos) => {
    if (result) return false;

    if (node.type.name === "changeRequest" && node.attrs.changeRequestId === id) {
      result = { from: pos, to: pos + node.nodeSize, node };
      return false;
    }

    return true;
  });

  return result;
}

export function collectChangeRequests(state: CommandProps["state"]): Array<{
  from: number;
  to: number;
  changeRequest: NormalizedChangeRequest;
}> {
  const result: Array<{
    from: number;
    to: number;
    changeRequest: NormalizedChangeRequest;
  }> = [];

  state.doc.descendants((node, pos) => {
    if (node.type.name === "changeRequest") {
      const changeRequest = normalizeChangeRequest(node);
      if (changeRequest) {
        result.push({ from: pos, to: pos + node.nodeSize, changeRequest });
      }
      return false;
    }
    return true;
  });

  return result;
}

export type ParentChangeRequestInfo = {
  changeRequestId: string | undefined;
  author: string | undefined;
  pendingAt: number | undefined;
};

export function getChangeRequestPendingAt(node: PMNode): number | undefined {
  return typeof node.attrs.pendingAt === "number" ? node.attrs.pendingAt : undefined;
}

export function getParentChangeRequest(
  editor: Editor,
  getPos: () => number | undefined,
): ParentChangeRequestInfo | null {
  const pos = getPos();
  if (typeof pos !== "number") return null;

  const { parent } = editor.state.doc.resolve(pos);
  if (parent.type.name !== "changeRequest") return null;

  return {
    changeRequestId:
      typeof parent.attrs.changeRequestId === "string" ? parent.attrs.changeRequestId : undefined,
    author: typeof parent.attrs.author === "string" ? parent.attrs.author : undefined,
    pendingAt: getChangeRequestPendingAt(parent),
  };
}

export function applyChangeRequestContent(
  tr: Transaction,
  from: number,
  to: number,
  content: Fragment | null,
) {
  if (content && content.size > 0) {
    tr.replaceWith(from, to, content);
  } else {
    tr.delete(from, to);
  }
}
